import { localePath } from "../i18n/index.js";
import { withBase } from "./services.js";

const STORAGE_KEY = "magallanes-cookie-consent";
const GA_MEASUREMENT_ID = "G-Q0JB5W73KD";
const GA_SCRIPT_SRC = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
const GA_DISABLE_KEY = `ga-disable-${GA_MEASUREMENT_ID}`;
const COOKIE_CONSENT_ENABLED = false;
const DEFAULT_PREFERENCES = Object.freeze({
  necessary: true,
  analytics: false,
});

const COPY = {
  es: {
    title: "Configuración de cookies",
    body: "Utilizamos cookies técnicas necesarias para que la web funcione y, solo si lo autorizas, cookies analíticas para medir el uso del sitio.",
    policy: "Política de cookies",
    manage: "Cookies",
    manageAria: "Abrir configuración de cookies",
    acceptAll: "Aceptar todo",
    rejectAll: "Rechazar",
    save: "Guardar selección",
    necessaryTitle: "Cookies necesarias",
    necessaryBody: "Siempre activas. Son imprescindibles para la navegación y el funcionamiento básico del sitio.",
    alwaysOn: "Siempre activas",
    analyticsTitle: "Cookies analíticas",
    analyticsBody: "Nos ayudan a entender cómo se usa la web mediante Google Analytics. Solo se instalarán si las aceptas.",
    analyticsOn: "Activadas",
    analyticsOff: "Desactivadas",
  },
  en: {
    title: "Cookie settings",
    body: "We use necessary technical cookies so the website can work and, only if you allow it, analytics cookies to measure site usage.",
    policy: "Cookie policy",
    manage: "Cookies",
    manageAria: "Open cookie settings",
    acceptAll: "Accept all",
    rejectAll: "Reject",
    save: "Save selection",
    necessaryTitle: "Necessary cookies",
    necessaryBody: "Always active. They are essential for navigation and the basic operation of the site.",
    alwaysOn: "Always active",
    analyticsTitle: "Analytics cookies",
    analyticsBody: "They help us understand how the website is used through Google Analytics. They will only be installed if you accept them.",
    analyticsOn: "Enabled",
    analyticsOff: "Disabled",
  },
};

let banner;
let manageButton;
let titleNode;
let bodyNode;
let policyLink;
let necessaryTitleNode;
let necessaryBodyNode;
let necessaryBadgeNode;
let analyticsTitleNode;
let analyticsBodyNode;
let analyticsCheckbox;
let analyticsInputLabel;
let analyticsStateNode;
let acceptButton;
let rejectButton;
let saveButton;
let analyticsInitPromise = null;
let currentLocale = "es";
let currentPreferences = { ...DEFAULT_PREFERENCES };
let onAcceptCallback = null;

function normalizePreferences(value) {
  if (value === "accepted") {
    return { ...DEFAULT_PREFERENCES, analytics: true };
  }

  if (value === "rejected") {
    return { ...DEFAULT_PREFERENCES, analytics: false };
  }

  if (!value || typeof value !== "object") {
    return null;
  }

  return {
    ...DEFAULT_PREFERENCES,
    analytics: Boolean(value.analytics),
  };
}

function getStoredPreferences() {
  try {
    const rawValue = window.localStorage.getItem(STORAGE_KEY);
    if (!rawValue) {
      return null;
    }

    if (rawValue === "accepted" || rawValue === "rejected") {
      return normalizePreferences(rawValue);
    }

    return normalizePreferences(JSON.parse(rawValue));
  } catch {
    return null;
  }
}

function hasStoredPreferences() {
  return getStoredPreferences() !== null;
}

function setStoredPreferences(preferences) {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        analytics: Boolean(preferences.analytics),
      })
    );
  } catch {
    // Ignore storage errors and keep the current session behavior.
  }
}

function deleteCookie(name) {
  const hostname = window.location.hostname;
  const hostParts = hostname.split(".");
  const domains = new Set([hostname]);

  if (hostParts.length >= 2) {
    domains.add(`.${hostParts.slice(-2).join(".")}`);
  }

  if (hostParts.length >= 3) {
    domains.add(`.${hostParts.slice(-3).join(".")}`);
  }

  domains.forEach((domain) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}; SameSite=Lax`;
  });

  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
}

function clearAnalyticsCookies() {
  const cookieNames = document.cookie
    .split(";")
    .map((cookie) => cookie.trim().split("=")[0])
    .filter(Boolean);

  cookieNames.forEach((name) => {
    if (name === "_ga" || name.startsWith("_ga_")) {
      deleteCookie(name);
    }
  });
}

function syncAnalyticsControl() {
  if (!analyticsCheckbox || !analyticsStateNode) {
    return;
  }

  analyticsCheckbox.checked = Boolean(currentPreferences.analytics);
  syncAnalyticsStateLabel();
}

function syncAnalyticsStateLabel() {
  if (!analyticsCheckbox || !analyticsStateNode) {
    return;
  }

  const copy = COPY[currentLocale];
  analyticsStateNode.textContent = analyticsCheckbox.checked ? copy.analyticsOn : copy.analyticsOff;
}

function hideBanner() {
  if (!banner) {
    return;
  }

  banner.hidden = true;
  banner.setAttribute("aria-hidden", "true");
}

function showBanner() {
  if (!banner) {
    return;
  }

  syncAnalyticsControl();
  banner.hidden = false;
  banner.setAttribute("aria-hidden", "false");
}

function updateManageButtonVisibility() {
  if (!manageButton) {
    return;
  }

  manageButton.hidden = !hasStoredPreferences();
}

function applyCopy(locale) {
  currentLocale = locale === "en" ? "en" : "es";

  if (
    !banner ||
    !manageButton ||
    !titleNode ||
    !bodyNode ||
    !policyLink ||
    !necessaryTitleNode ||
    !necessaryBodyNode ||
    !necessaryBadgeNode ||
    !analyticsTitleNode ||
    !analyticsBodyNode ||
    !analyticsInputLabel ||
    !analyticsStateNode ||
    !acceptButton ||
    !rejectButton ||
    !saveButton
  ) {
    return;
  }

  const copy = COPY[currentLocale];
  titleNode.textContent = copy.title;
  bodyNode.textContent = copy.body;
  policyLink.textContent = copy.policy;
  policyLink.href = withBase(localePath(currentLocale, "/politica-de-cookies"));
  necessaryTitleNode.textContent = copy.necessaryTitle;
  necessaryBodyNode.textContent = copy.necessaryBody;
  necessaryBadgeNode.textContent = copy.alwaysOn;
  analyticsTitleNode.textContent = copy.analyticsTitle;
  analyticsBodyNode.textContent = copy.analyticsBody;
  analyticsInputLabel.setAttribute("aria-label", copy.analyticsTitle);
  acceptButton.textContent = copy.acceptAll;
  rejectButton.textContent = copy.rejectAll;
  saveButton.textContent = copy.save;
  manageButton.textContent = copy.manage;
  manageButton.setAttribute("aria-label", copy.manageAria);
  syncAnalyticsControl();
}

function ensureAnalytics() {
  if (analyticsInitPromise) {
    return analyticsInitPromise;
  }

  analyticsInitPromise = new Promise((resolve, reject) => {
    window[GA_DISABLE_KEY] = false;
    window.dataLayer = window.dataLayer || [];

    function gtag() {
      window.dataLayer.push(arguments);
    }

    window.gtag = window.gtag || gtag;

    const existingScript = document.querySelector(`script[src="${GA_SCRIPT_SRC}"]`);
    const finishInit = () => {
      window.gtag("js", new Date());
      window.gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });
      resolve();
    };

    if (existingScript) {
      finishInit();
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.src = GA_SCRIPT_SRC;
    script.addEventListener("load", finishInit, { once: true });
    script.addEventListener("error", () => reject(new Error("No se pudo cargar Google Analytics.")), { once: true });
    document.head.appendChild(script);
  });

  return analyticsInitPromise;
}

function enableAnalytics({ trackPageView = false } = {}) {
  ensureAnalytics()
    .then(() => {
      if (trackPageView) {
        onAcceptCallback?.();
      }
    })
    .catch(() => {
      // Keep the chosen preference even if analytics cannot load.
    });
}

function disableAnalytics() {
  window[GA_DISABLE_KEY] = true;
  clearAnalyticsCookies();
}

function applyPreferences(preferences, { trackPageView = false } = {}) {
  currentPreferences = {
    ...DEFAULT_PREFERENCES,
    analytics: Boolean(preferences.analytics),
  };

  setStoredPreferences(currentPreferences);
  syncAnalyticsControl();

  if (currentPreferences.analytics) {
    enableAnalytics({ trackPageView });
  } else {
    disableAnalytics();
  }

  hideBanner();
  updateManageButtonVisibility();
}

function createPreferenceRow({ titleRef, bodyRef, controlNode, alwaysOn = false }) {
  const row = document.createElement("div");
  row.className = "cookie-consent__row";

  const copyWrap = document.createElement("div");
  copyWrap.className = "cookie-consent__row-copy";

  titleRef = document.createElement("h3");
  titleRef.className = "cookie-consent__row-title";

  bodyRef = document.createElement("p");
  bodyRef.className = "cookie-consent__row-body";

  copyWrap.append(titleRef, bodyRef);

  const controlWrap = document.createElement("div");
  controlWrap.className = "cookie-consent__row-control";

  if (alwaysOn) {
    const badge = document.createElement("span");
    badge.className = "cookie-consent__badge";
    controlWrap.appendChild(badge);
    row.append(copyWrap, controlWrap);
    return { row, titleRef, bodyRef, badgeRef: badge };
  }

  controlWrap.appendChild(controlNode);
  row.append(copyWrap, controlWrap);
  return { row, titleRef, bodyRef };
}

function createSwitch() {
  const wrapper = document.createElement("div");
  wrapper.className = "cookie-consent__switch-wrap";

  analyticsStateNode = document.createElement("span");
  analyticsStateNode.className = "cookie-consent__switch-state";

  const label = document.createElement("label");
  label.className = "cookie-consent__switch";

  const input = document.createElement("input");
  input.type = "checkbox";
  input.className = "cookie-consent__switch-input";

  const track = document.createElement("span");
  track.className = "cookie-consent__switch-track";
  track.setAttribute("aria-hidden", "true");

  label.append(input, track);

  analyticsCheckbox = input;
  analyticsInputLabel = input;
  input.addEventListener("change", syncAnalyticsStateLabel);
  wrapper.append(analyticsStateNode, label);

  return wrapper;
}

function createBanner() {
  banner = document.createElement("section");
  banner.className = "cookie-consent";
  banner.setAttribute("aria-label", "Cookie consent");

  titleNode = document.createElement("h2");
  titleNode.className = "cookie-consent__title";

  bodyNode = document.createElement("span");
  bodyNode.className = "cookie-consent__body";

  policyLink = document.createElement("a");
  policyLink.className = "cookie-consent__link";

  const intro = document.createElement("div");
  intro.className = "cookie-consent__copy";
  intro.append(bodyNode, document.createTextNode(" "), policyLink);

  const necessaryRow = createPreferenceRow({
    alwaysOn: true,
  });
  necessaryTitleNode = necessaryRow.titleRef;
  necessaryBodyNode = necessaryRow.bodyRef;
  necessaryBadgeNode = necessaryRow.badgeRef;

  const analyticsRow = createPreferenceRow({
    controlNode: createSwitch(),
  });
  analyticsTitleNode = analyticsRow.titleRef;
  analyticsBodyNode = analyticsRow.bodyRef;

  const preferences = document.createElement("div");
  preferences.className = "cookie-consent__preferences";
  preferences.append(necessaryRow.row, analyticsRow.row);

  rejectButton = document.createElement("button");
  rejectButton.type = "button";
  rejectButton.className = "cookie-consent__btn cookie-consent__btn--secondary";
  rejectButton.addEventListener("click", () => {
    applyPreferences({ analytics: false });
  });

  saveButton = document.createElement("button");
  saveButton.type = "button";
  saveButton.className = "cookie-consent__btn cookie-consent__btn--ghost";
  saveButton.addEventListener("click", () => {
    applyPreferences({ analytics: analyticsCheckbox.checked }, { trackPageView: analyticsCheckbox.checked });
  });

  acceptButton = document.createElement("button");
  acceptButton.type = "button";
  acceptButton.className = "cookie-consent__btn cookie-consent__btn--primary";
  acceptButton.addEventListener("click", () => {
    analyticsCheckbox.checked = true;
    applyPreferences({ analytics: true }, { trackPageView: true });
  });

  const actions = document.createElement("div");
  actions.className = "cookie-consent__actions";
  actions.append(rejectButton, saveButton, acceptButton);

  banner.append(titleNode, intro, preferences, actions);
  document.body.appendChild(banner);

  manageButton = document.createElement("button");
  manageButton.type = "button";
  manageButton.className = "cookie-consent-toggle";
  manageButton.addEventListener("click", () => {
    showBanner();
    manageButton.hidden = true;
  });
  document.body.appendChild(manageButton);
}

export function initCookieConsent({ locale = "es", onAccept } = {}) {
  if (!COOKIE_CONSENT_ENABLED) {
    onAcceptCallback = onAccept || null;
    window[GA_DISABLE_KEY] = true;
    return;
  }

  onAcceptCallback = onAccept || null;
  createBanner();
  applyCopy(locale);

  const storedPreferences = getStoredPreferences();
  if (storedPreferences) {
    currentPreferences = storedPreferences;
    syncAnalyticsControl();

    if (storedPreferences.analytics) {
      enableAnalytics({ trackPageView: true });
    } else {
      disableAnalytics();
    }

    hideBanner();
  } else {
    currentPreferences = { ...DEFAULT_PREFERENCES };
    syncAnalyticsControl();
    showBanner();
  }

  updateManageButtonVisibility();
}

export function updateCookieConsentLocale(locale) {
  if (!COOKIE_CONSENT_ENABLED) {
    return;
  }

  applyCopy(locale);
}

export function canTrackAnalytics() {
  if (!COOKIE_CONSENT_ENABLED) {
    return false;
  }

  return Boolean(getStoredPreferences()?.analytics);
}
